// structure of json's post request
// {
//   "solution": {
//     "contest_id": 1,
//     "title": "title",
//     "summary": "summary",
//     "special_terms": "special_terms",
//     "user_id": 1,
//     "supplemental_submission_answers": [
//       {
//         "id": 1, if available
//         "answer": "answer",
//         "supplemental_submission_field_id": 1,
//         "answer": []
//       }
//     ],
//     "bibliographies_attributes": [
//       {
//         "id": 1, if available
//         "source": "source",
//         "_destroy": "_destroy" if available
//       }
//     ],
//     "solutionfiles_attributes": [
//       {
//         "_destroy": "_destroy", if needed
//         "key": "key"
//       }
//     ]
//   }
// }

import React, { useCallback, useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";

import useCurrentUser from "../../../hooks/useCurrentUser";
import useContestSettings from "../../../hooks/useContestSettings";
import AlertPanel from "../alertPanel/alertPanel.component";
import Field, { TYPES } from "../formFields/field.component";
import UserField, {
  TYPES as USER_FIELD_TYPES,
} from "../../users/formFields/field.component";
import SolutionFormProvider from "../../../context/solutionFormContext";

import { FormWrapper, GroupsWrapper } from "./form.style";

import { getText } from "../../../utils";

const Form = () => {
  const wrapperRef = useRef();
  const currentUser = useCurrentUser();

  const {
    formObject: form,
    data,
    formRef,
    handleFormSubmit,
    handleSaveDraft,
    formDisabled,
    submitForm,
    setFormValid,
    formValid,
  } = useContestSettings();

  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      solution: {
        contest_id: data.id,
        user_id: currentUser?.id,
        title: form?.solutionTitle?.draftValue || "",
        supplemental_submission_answers:
          form?.supplementalSubmissionFields?.flatMap((group) =>
            group.values.map((formField) => ({
              answer: formField.draftValue || "",
              supplemental_submission_field_id: formField.id,
            })),
          ),
        bibliographies: [...Array(form?.maxBibliographiesCount || 0)].map(
          () => ({
            source: "",
          }),
        ),
        solutionfiles:
          form?.solutionfiles?.map((file) => ({
            ...file,
          })) || [],
      },
      user: form?.userProfileFields?.reduce((a, v) => {
        if (Object.prototype.hasOwnProperty.call(v, "additionalField")) {
          return { ...a, [v.name]: "", [v.additionalField]: "" };
        }
        return { ...a, [v.name]: "" };
      }, {}),
      spot_id: form?.reservedSpotId,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    control,
    setError,
    clearErrors,
  } = formMethods;

  const watchAllFields = useWatch({ control });

  const getDeliverablesFields = () => {
    let index = 0;
    let deliverables = [];
    form?.supplementalSubmissionFields?.forEach((group) => {
      group.values.forEach((field) => {
        if (field.type === "deliverable") {
          deliverables.push(
            `solution.supplemental_submission_answers.${index}.answer`,
          );
        }
        index++;
      });
    });
    return deliverables;
  };
  const watchDeliverables = useWatch({
    control,
    name: getDeliverablesFields(),
  });

  useEffect(() => {
    if (!form?.copyPasteRestricted) return;
    if (getDeliverablesFields().length === 0) return;
    let charCount = 0;
    watchDeliverables.forEach((value) => {
      charCount += getText(value).length;
    });
    if (charCount <= 200) {
      setError("deliverables_min_length", {
        type: "required",
        message: "Deliverables must be at least 200 characters long",
      });
    } else {
      clearErrors("deliverables_min_length");
    }
  }, [watchDeliverables]);

  const autoSaveDraft = useCallback(
    throttle((values) => {
      debounce((values) => {
        handleSaveDraft(values);
      }, 200)(values);
    }, 5000),
    [],
  );

  useEffect(() => {
    if (!currentUser) return;
    if (!isDirty) return;
    autoSaveDraft(watchAllFields);
  }, [isDirty, watchAllFields, autoSaveDraft, currentUser]);

  useEffect(() => {
    const deliverablesValid = errors?.deliverables_min_length ? false : true;
    setFormValid(deliverablesValid && isValid);
  }, [isValid, errors?.deliverables_min_length]);

  useEffect(() => {
    window.scrollTo({
      top: wrapperRef.current.offsetTop - 200,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const isPrefilter = form?.formType === "prefilter_form";

  let reversedIndex =
    (form?.userProfileFields?.length || 0) +
    (form?.supplementalSubmissionFields?.flatMap((group) => group.values)
      ?.length || 0);

  const renderUsersDemographics = () => {
    return form?.userProfileFields?.map((formItem, index) => (
      <div
        key={index++}
        style={{ position: "relative", zIndex: reversedIndex-- }}
      >
        <UserField
          type={USER_FIELD_TYPES[formItem.type]}
          formMethods={formMethods}
          error={errors?.user?.[formItem.name]}
          {...formItem}
          name={`user.${formItem.name}`}
        />
      </div>
    ));
  };

  const renderFields = () => {
    let index = -1;
    return form?.supplementalSubmissionFields.map((groupItem, gidx) => (
      <div key={gidx} className={groupItem.groupName ? "grouped" : ""}>
        <div className="title">{groupItem.groupName}</div>
        <div>
          {groupItem.values.map((formItem) => (
            <div
              key={index++}
              style={{ position: "relative", zIndex: reversedIndex-- }}
            >
              <Field
                index={index}
                type={formItem.fieldType}
                data={formItem}
                name={`solution.supplemental_submission_answers.${index}`}
                error={
                  errors?.solution?.supplemental_submission_answers?.[index]
                    ?.answer
                }
              />
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <FormWrapper className="row p-0 mt-5" ref={wrapperRef}>
      {form.generateAiSolution && (
        <AlertPanel message={form.generateAiMessage} />
      )}

      <div id="FormPanel" className="col-12 p-3">
        <div
          id="YourSubmission"
          className="form-heading mb-2 mt-4"
          data-anchor="Your Submission"
        >
          Your Submission
        </div>
        <div className="form-instructions pb-1">
          All fields are mandatory, unless marked as optional.
        </div>
        <div>
          <form onSubmit={handleSubmit(handleFormSubmit)} ref={formRef}>
            <SolutionFormProvider
              formMethods={formMethods}
              disableRestrictedFields={formDisabled}
              copyPasteRestricted={form?.copyPasteRestricted}
            >
              <input
                type="hidden"
                value={data.id}
                {...register("solution.contest_id")}
              />
              <input type="hidden" {...register("solution.user_id")} />
              {!isPrefilter && (
                <>
                  <Field
                    type={TYPES.TITLE}
                    data={{
                      required: true,
                      title: "Title",
                      placeholder: "Title",
                      maxCharacters: 250,
                    }}
                    name="solution.title"
                    error={errors?.solution?.title}
                  />
                  {renderUsersDemographics()}
                </>
              )}
              <GroupsWrapper>{renderFields()}</GroupsWrapper>
              {errors?.deliverables_min_length && (
                <span className="error">
                  {errors?.deliverables_min_length?.message || ""}
                </span>
              )}
              {form?.maxBibliographiesCount > 0 && (
                <div className="sources">
                  <div>Sources</div>
                  {[...Array(form?.maxBibliographiesCount || 0)].map(
                    (_, idx) => (
                      <Field
                        key={idx}
                        type={TYPES.SIMPLE_TEXT}
                        data={{
                          required: false,
                          title: null,
                          placeholder: "Source",
                        }}
                        name={`solution.bibliographies.${idx}.source`}
                        error={errors?.solution?.bibliographies?.[idx]?.source}
                      />
                    ),
                  )}
                </div>
              )}
              {form?.allowUploads && (
                <div>
                  <div>Files</div>
                  <Field
                    type={TYPES.FILE}
                    name="solution.solutionfiles"
                    error={errors?.solutionfiles}
                  />
                </div>
              )}
              {form?.includePlagiarismStatements && (
                <div className="field-block">
                  <Field
                    type={TYPES.CONSENT_CHECKBOX}
                    data={{
                      required: true,
                      option:
                        "I certify that my solution has not plagirized other sources. The content is my own and I have the right to submit it.",
                    }}
                    name="plagiarism.1"
                    error={errors?.plagiarism?.[1]}
                  />
                  <Field
                    type={TYPES.CONSENT_CHECKBOX}
                    data={{
                      required: true,
                      option:
                        "I understand that if plagiarized material is discovered in my solution, it may result in the removal of my MindSumo account.",
                    }}
                    name="plagiarism.2"
                    error={errors?.plagiarism?.[2]}
                  />
                </div>
              )}
            </SolutionFormProvider>
          </form>
          <button
            className={`btn btn-primary rounded px-3 py-2${formValid ? "" : " disabled"}`}
            onClick={submitForm}
          >
            Submit solution
          </button>
        </div>
      </div>
    </FormWrapper>
  );
};

export default Form;
